/**
 * Service to manage the browser page title based on test name, question number, and question type.
 * This improves accessibility by making each page have a unique, descriptive title.
 */

import assessmentReader from '../store/readHelpers/AssessmentReader';
import questionReader from '../store/readHelpers/QuestionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import storeManager from '../store/StoreManager';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from './a11yIIBScreenReaderService';

class PageTitleService {
    constructor() {
        // Store the original page title on initialization to use as fallback
        // This prevents the cascading title bug when using document.title later
        this.originalPageTitle = null;
    }

    /**
     * Updates the browser's document title based on current context
     * Only updates if a11y IIB Screen Reader v4 or higher is supported
     */
    updatePageTitle() {
        // Feature guard: only update if a11y IIB Screen Reader v4 or higher is supported
        if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4)) {
            return;
        }

        // Capture original page title on first call if not already stored
        if (!this.originalPageTitle) {
            this.originalPageTitle = document.title || 'Inspera Assessment';
        }

        const title = this.generatePageTitle();
        if (title && document.title !== title) {
            document.title = title;
        }
    }

    /**
     * Generates the appropriate page title based on current state
     * @returns {string} The generated page title
     */
    generatePageTitle() {
        const state = storeManager.getState();

        // Get test name from assessment (pass state to avoid redundant getState() call)
        const assessment = assessmentReader.getAssessment(state);
        const testName = assessment && assessment.title ? assessment.title : null;

        const { currentlyShown } = state;
        const questionId = currentlyShown ? currentlyShown.questionId : null;

        // Get the base title to use as fallback
        // Use stored original title if available, otherwise use static fallback
        // Do NOT read document.title here as it may be polluted by previous operations
        const baseFallbackTitle = this.originalPageTitle || 'Inspera Assessment';

        if (!questionId) {
            // If no question is shown, use test name or base fallback
            return testName || baseFallbackTitle;
        }

        // For question pages, use test name or base fallback
        const finalTestName = testName || baseFallbackTitle;

        const question = assessmentReader.getQuestion(questionId, state);

        if (!question) {
            return finalTestName;
        }

        // Check if this is a document or form type
        if (questionReader.isDocument(question)) {
            const documentLabel = this.getDocumentFormLabel(question, 'Document');
            return `${finalTestName} - ${documentLabel}`;
        }

        if (questionReader.isForm(question)) {
            const formLabel = this.getDocumentFormLabel(question, 'Form');
            return `${finalTestName} - ${formLabel}`;
        }

        // For regular questions: "Test Name - 1" or "Test Name - 1a" or "Test Name - 1.1"
        const questionNumber = this.getQuestionNumber(question);
        if (questionNumber) {
            return `${finalTestName} - ${questionNumber}`;
        }

        // Fallback
        return finalTestName;
    }

    /**
     * Gets the label for document/form types with section number prefix if applicable
     * @param {Object} question - The question object
     * @param {string} type - Either 'Document' or 'Form'
     * @returns {string} The formatted label
     */
    getDocumentFormLabel(question, type) {
        if (!type) {
            return '';
        }
        // For section-based numbering schemes, prefix with section number
        if (settingsReader.useSectionAlphaNumeric() || settingsReader.useSectionNumeric()) {
            const sectionNum = question.sectionNumber || '';
            if (sectionNum) {
                return `${sectionNum}.${type}`;
            }
        }

        // For test sequential (or if no section number), just return the type
        return type;
    }

    /**
     * Gets a complete question number suitable for use in page titles.
     * Formats: Test Sequential (1, 2, 3), Section Alphanumeric (1a, 2b), Section Numeric (1.1, 2.2)
     * @param {Object} question - The question object
     * @param {Object} state - Redux state (optional for backward compatibility)
     * @returns {string|number|null} The formatted question number or null
     */
    getQuestionNumber(question) {
        if (!question) {
            return null;
        }

        // Per scorable item (numbering per interaction)
        if (scorableItemNumberingService.useNumberPerInteraction()) {
            const questionNumber = scorableItemNumberingService.getQuestionNumber(question.contentRevisionId);
            return questionNumber || question.number;
        }

        // Section alphanumeric (1a, 1b, 2a, 2b, etc.)
        if (settingsReader.useSectionAlphaNumeric()) {
            const sectionNum = question.sectionNumber;
            const letterPart = question.questionNumberingInSection;

            if (sectionNum && letterPart) {
                if (question.subQuestionNumber) {
                    return `${sectionNum}${letterPart} (${question.subQuestionNumber})`;
                }
                return `${sectionNum}${letterPart}`;
            }
        }

        // Section numeric (1.1, 1.2, 2.1, 2.2, etc.)
        if (settingsReader.useSectionNumeric()) {
            const sectionNum = question.sectionNumber;
            const questionNum = question.questionNumberingInSection;

            if (sectionNum && questionNum) {
                if (question.subQuestionNumber) {
                    return `${sectionNum}.${questionNum} (${question.subQuestionNumber})`;
                }
                return `${sectionNum}.${questionNum}`;
            }

            // Fallback: use questionIndex if questionNumberingInSection is not available
            if (sectionNum && question.questionIndex !== undefined) {
                return `${sectionNum}.${question.questionIndex + 1}`;
            }
        }

        // Test sequential (1, 2, 3, etc.) - default
        return question.number;
    }
}

const pageTitleService = new PageTitleService();
export default pageTitleService;
